import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { rupiah } from "@/lib/format";
import FilterBar from "@/components/FilterBar";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, Cell, Legend } from "recharts";
import { TrendingUp, Trophy, Wallet, Receipt, Target, Download, Award, ArrowUpRight, ArrowDownRight, Crown } from "lucide-react";

const ts = { background: "#18181B", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12 };
// Recharts does not read contentStyle for the label/item text color — without these,
// tooltip text silently inherits the page's default (near-black in light mode),
// landing dark-on-dark against the tooltip's own dark background.
const tsItem = { color: "#e5e7eb" };
const tsLabel = { color: "#93c5fd" };

// Shrink-to-fit stat number (same nowrap+clamp+tabular-nums safety net as the Kpi cards)
// for the ad-hoc number displays below (Target vs Actual, Growth YoY) that aren't inside
// a Kpi card — prevents a large Rupiah figure from wrapping mid-number/overlapping its label.
function Stat({ label, value, color, size = "lg", align = "left" }) {
  const px = size === "2xl" ? "clamp(1rem, 3vw, 1.5rem)" : "clamp(0.85rem, 2.6vw, 1.125rem)";
  return (
    <div className={align === "right" ? "text-right" : ""}>
      <div className="text-xs text-zinc-500">{label}</div>
      <div className="font-bold font-heading whitespace-nowrap overflow-hidden text-ellipsis" style={{ fontSize: px, color, fontVariantNumeric: "tabular-nums" }} title={typeof value === "string" ? value : undefined}>{value}</div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent, sub }) {
  return (
    <div className="card-surface card-tint rounded-2xl p-4" style={{ "--tint": accent }}>
      <div className="w-9 h-9 rounded-full flex items-center justify-center mb-2" style={{ background: `${accent}22`, color: accent }}><Icon size={18} /></div>
      <div className="font-bold font-heading tracking-tight whitespace-nowrap overflow-hidden text-ellipsis"
        style={{ fontSize: "clamp(0.7rem, 2.5vw, 0.88rem)", fontVariantNumeric: "tabular-nums" }} title={typeof value === "string" ? value : undefined}>{value}</div>
      <div className="text-xs text-zinc-500 mt-0.5">{label}</div>
      {sub && <div className="text-[11px] text-zinc-600 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function Reports() {
  const [qs, setQs] = useState("");
  const year = new URLSearchParams(qs).get("year") || String(new Date().getFullYear());
  const { data, isLoading } = useQuery({ queryKey: ["omset", qs], queryFn: async () => (await api.get(`/reports/omset?${qs}`)).data });
  const { data: am } = useQuery({ queryKey: ["admin-monthly", year], queryFn: async () => (await api.get(`/reports/admin-monthly?year=${year}`)).data });

  return (
    <div className="px-4 py-5 max-w-5xl mx-auto print-area md:max-w-none md:px-8">
      <div className="flex items-start justify-between mb-1">
        <div>
          <h1 className="text-xl font-bold font-heading tracking-tight">Rekap Omset & Penjualan</h1>
          <p className="text-sm text-zinc-500 mb-1">Omset, target vs actual, growth YoY, dan tren per admin</p>
        </div>
        <button data-testid="print-rekap" onClick={() => window.print()} className="no-print shrink-0 flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#18181B] border border-white/10 text-sm text-zinc-300 hover:text-white"><Download size={15} />Unduh PDF</button>
      </div>
      <div className="no-print"><FilterBar onChange={setQs} /></div>

      {isLoading || !data ? null : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <Kpi icon={TrendingUp} label="Omset Perencanaan/Design" value={rupiah(data.kpis.omset_perencanaan ?? data.kpis.total_omset)} accent="#10B981" />
            <Kpi icon={TrendingUp} label="Omset Produksi" value={rupiah(data.kpis.omset_produksi || 0)} accent="#8B5CF6" />
            <Kpi icon={Trophy} label="Total Closing" value={data.kpis.total_closing} accent="#3B82F6" />
            <Kpi icon={Receipt} label="Sudah Dibayar" value={rupiah(data.kpis.total_dibayar)} accent="#22D3EE" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <Kpi icon={Wallet} label="Sisa Piutang Design" value={rupiah(data.kpis.total_piutang)} accent="#F59E0B" />
            <Kpi icon={Wallet} label="Sisa Piutang Produksi" value={rupiah(data.kpis.total_piutang_produksi || 0)} accent="#EF4444" />
            <Kpi icon={Trophy} label="Omset Gabungan" value={rupiah(data.kpis.omset_gabungan ?? data.kpis.total_omset)} accent="#EAB308" />
            <Kpi icon={Wallet} label="Sisa Piutang Gabungan" value={rupiah(data.kpis.total_piutang_gabungan ?? data.kpis.total_piutang)} accent="#EAB308" />
          </div>

          {/* Target vs Actual + YoY */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
            <div className="card-surface rounded-2xl p-5">
              <h3 className="font-heading font-semibold mb-3 text-sm uppercase tracking-wide text-zinc-400">Target Perusahaan — Periode Berjalan</h3>
              <div className="flex items-end gap-4 sm:gap-6 mb-3 flex-wrap">
                <Stat label="Actual" value={rupiah(data.target.actual)} color="#22d3ee" />
                <Stat label="Target" value={rupiah(data.target.target)} color="#a1a1aa" />
                <Stat label="Gap" value={`${(data.target.gap ?? (data.target.actual - data.target.target)) >= 0 ? "+" : ""}${rupiah(data.target.gap ?? (data.target.actual - data.target.target))}`}
                  color={(data.target.gap ?? (data.target.actual - data.target.target)) >= 0 ? "#10B981" : "#EF4444"} />
                <div className="ml-auto">
                  <Stat label="Achievement" value={`${data.target.achievement_pct}%`} size="2xl" align="right"
                    color={data.target.achievement_pct >= 100 ? "#10B981" : data.target.achievement_pct >= 70 ? "#F59E0B" : "#EF4444"} />
                </div>
              </div>
              <div className="h-3 rounded-full bg-[#18181B] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.min(100, data.target.achievement_pct)}%`, background: data.target.achievement_pct >= 100 ? "#10B981" : data.target.achievement_pct >= 70 ? "#F59E0B" : "#EF4444" }} /></div>
              {!data.target.target ? <p className="text-[11px] text-zinc-500 mt-2">Belum ada Target Perusahaan untuk periode ini — atur di Target &amp; Komisi &gt; mode Perusahaan.</p> : null}
            </div>
            <div className="card-surface rounded-2xl p-5">
              <h3 className="font-heading font-semibold mb-3 text-sm uppercase tracking-wide text-zinc-400">Growth YoY ({data.yoy.ref_year} vs {data.yoy.ref_year - 1})</h3>
              <div className="flex items-end gap-4 flex-wrap">
                <Stat label="Tahun Ini" value={rupiah(data.yoy.this_year)} color="#34d399" />
                <Stat label="Tahun Lalu" value={rupiah(data.yoy.last_year)} color="#a1a1aa" />
                <div className={`ml-auto flex items-center gap-1 shrink-0 font-bold font-heading whitespace-nowrap ${data.yoy.growth_pct >= 0 ? "text-emerald-400" : "text-red-400"}`}
                  style={{ fontSize: "clamp(0.9rem, 2.6vw, 1.25rem)", fontVariantNumeric: "tabular-nums" }}>
                  {data.yoy.growth_pct >= 0 ? <ArrowUpRight size={18} className="shrink-0" /> : <ArrowDownRight size={18} className="shrink-0" />}{data.yoy.growth_pct}%
                </div>
              </div>
            </div>
          </div>

          <div className="card-surface rounded-2xl p-5 mb-4">
            <h3 className="font-heading font-semibold mb-4 text-sm uppercase tracking-wide text-zinc-400">Chart: Tahun Lalu vs Tahun Ini (per bulan)</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.yoy.monthly_compare}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="month" tick={{ fill: "#71717a", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#71717a", fontSize: 10 }} axisLine={false} tickLine={false} width={45} tickFormatter={(v) => v >= 1e6 ? (v / 1e6) + "jt" : v} />
                <Tooltip contentStyle={ts} itemStyle={tsItem} labelStyle={tsLabel} formatter={(v) => rupiah(v)} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="last_year" name={`${data.yoy.ref_year - 1}`} fill="#3f3f46" radius={[4, 4, 0, 0]} />
                <Bar dataKey="this_year" name={`${data.yoy.ref_year}`} fill="#3B82F6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Admin monthly trend (kontes antar sales) */}
          {am && (
            <div className="card-surface rounded-2xl p-5 mb-4">
              <h3 className="font-heading font-semibold mb-3 text-sm uppercase tracking-wide text-zinc-400">Rekap Penjualan per Admin — Tren Bulanan {am.year}</h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                <Kpi icon={TrendingUp} label="Total Tahun Ini" value={rupiah(am.grand_total)} accent="#10B981" />
                <Kpi icon={Crown} label="Admin Terbaik" value={am.best_admin} accent="#F59E0B" />
                <Kpi icon={Award} label="Bulan Terbaik" value={am.best_month} accent="#3B82F6" />
                <Kpi icon={Target} label="Rata-rata/Admin" value={rupiah(am.avg_per_admin)} accent="#22D3EE" />
              </div>
              <div className="overflow-x-auto hide-scrollbar">
                <table className="w-full text-xs min-w-[820px]">
                  <thead><tr className="text-zinc-500 uppercase border-b border-white/5">
                    <th className="text-left py-2 px-2 font-medium sticky left-0 bg-[#121214]">Admin</th>
                    {am.months.map((m) => <th key={m} className="text-right font-medium px-1">{m}</th>)}
                    <th className="text-right font-medium px-2">Total</th>
                    <th className="text-right font-medium px-2">Terbaik</th>
                  </tr></thead>
                  <tbody>
                    {am.rows.map((r, ri) => (
                      <tr key={ri} className="border-b border-white/5">
                        <td className="py-2 px-2 font-medium whitespace-nowrap sticky left-0 bg-[#121214]">{ri === 0 && <Crown size={12} className="inline mr-1 text-amber-400" />}{r.name}</td>
                        {r.months.map((v, mi) => <td key={mi} className={`text-right px-1 ${v > 0 ? "text-zinc-200" : "text-zinc-700"}`}>{v > 0 ? (v / 1e6).toLocaleString("id-ID") : "-"}</td>)}
                        <td className="text-right px-2 font-semibold text-emerald-400 whitespace-nowrap">{rupiah(r.total)}</td>
                        <td className="text-right px-2 text-blue-300">{r.best_month}</td>
                      </tr>
                    ))}
                    <tr className="font-semibold">
                      <td className="py-2 px-2 sticky left-0 bg-[#121214]">Total</td>
                      {am.totals.map((v, i) => <td key={i} className="text-right px-1 text-zinc-300">{v > 0 ? (v / 1e6).toLocaleString("id-ID") : "-"}</td>)}
                      <td className="text-right px-2 text-emerald-400 whitespace-nowrap">{rupiah(am.grand_total)}</td><td></td>
                    </tr>
                  </tbody>
                </table>
                <p className="text-[10px] text-zinc-600 mt-2">*Angka tabel dalam juta (jt) agar muat; total dalam Rupiah penuh.</p>
              </div>
              <ResponsiveContainer width="100%" height={220} className="mt-4">
                <LineChart data={am.months.map((m, i) => { const row = { month: m }; am.rows.forEach((r) => { row[r.name] = r.months[i]; }); return row; })}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="month" tick={{ fill: "#71717a", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#71717a", fontSize: 10 }} axisLine={false} tickLine={false} width={45} tickFormatter={(v) => v >= 1e6 ? (v / 1e6) + "jt" : v} />
                  <Tooltip contentStyle={ts} itemStyle={tsItem} labelStyle={tsLabel} formatter={(v) => rupiah(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {am.rows.map((r, i) => <Line key={r.name} type="monotone" dataKey={r.name} stroke={["#3B82F6", "#22D3EE", "#F59E0B", "#10B981"][i % 4]} strokeWidth={2} dot={{ r: 3 }} />)}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="card-surface rounded-2xl p-5 mb-4">
            <h3 className="font-heading font-semibold mb-4 text-sm uppercase tracking-wide text-zinc-400">Rekap Penjualan per Sales (periode)</h3>
            <div className="overflow-x-auto hide-scrollbar">
              <table className="w-full text-sm min-w-[760px]">
                <thead><tr className="text-zinc-500 text-xs uppercase border-b border-white/5">
                  <th className="text-left py-2 font-medium">Sales</th><th className="text-right font-medium">Leads</th><th className="text-right font-medium">Closing</th><th className="text-right font-medium">Rate</th><th className="text-right font-medium">Omset Design</th><th className="text-right font-medium">Omset Produksi</th><th className="text-right font-medium">Piutang Gabungan</th><th className="text-right font-medium">Komisi Gabungan</th>
                </tr></thead>
                <tbody>
                  {data.per_sales.map((s, i) => (
                    <tr key={i} data-testid={`sales-row-${i}`} className="border-b border-white/5">
                      <td className="py-2.5 font-medium">{s.name}</td><td className="text-right text-zinc-400">{s.leads}</td><td className="text-right text-zinc-400">{s.closing}</td><td className="text-right text-cyan-400">{s.closing_rate}%</td><td className="text-right font-semibold text-emerald-400">{rupiah(s.omset)}</td><td className="text-right font-semibold text-purple-400">{rupiah(s.omset_produksi || 0)}</td><td className="text-right text-amber-400">{rupiah(s.piutang_gabungan ?? s.piutang)}</td><td className="text-right text-blue-300">{rupiah(s.commission_gabungan ?? s.commission)}</td>
                    </tr>
                  ))}
                  {data.per_sales.length === 0 && <tr><td colSpan={8} className="text-center text-zinc-600 py-6">Tidak ada data</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
